import React from 'react';

// Reusable period picker for Products (Phase 11) — matches the Dashboard's
// preset pill row + custom range popover style so the interface stays
// consistent across screens.
//
// Value shape (state managed by the parent):
//   { period: 'this_month' | 'today' | 'this_week' | 'custom',
//     date_from: 'YYYY-MM-DD' | undefined,
//     date_to:   'YYYY-MM-DD' | undefined }

const PRESETS = [
    { key: 'today',      label: "Aujourd'hui" },
    { key: 'this_week',  label: 'Cette semaine' },
    { key: 'this_month', label: 'Ce mois' },
    { key: 'custom',     label: 'Personnalisé' },
];

function fmtDayFR(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return iso; }
}

export default function PeriodPicker({ value, onChange, testId = 'products-period' }) {
    const { period = 'this_month', date_from, date_to } = value || {};
    const [openCustom, setOpenCustom] = React.useState(false);
    const [draftFrom, setDraftFrom] = React.useState(date_from || '');
    const [draftTo, setDraftTo] = React.useState(date_to || '');

    const selectPreset = (k) => {
        if (k === 'custom') { setOpenCustom(true); return; }
        onChange({ period: k });
    };

    const applyCustom = () => {
        if (!draftFrom || !draftTo) return;
        onChange({ period: 'custom', date_from: draftFrom, date_to: draftTo });
        setOpenCustom(false);
    };

    const customLabel = period === 'custom' && date_from && date_to
        ? `${fmtDayFR(date_from)} → ${fmtDayFR(date_to)}`
        : 'Personnalisé';

    return (
        <div data-testid={testId} className="inline-flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-lg p-1 shadow-[0_1px_2px_rgba(16,24,40,0.04)] relative">
            {PRESETS.map((p) => {
                const active = p.key === period;
                const label = p.key === 'custom' ? customLabel : p.label;
                return (
                    <button
                        key={p.key}
                        type="button"
                        data-testid={`${testId}-${p.key}`}
                        onClick={() => selectPreset(p.key)}
                        className={
                            'h-8 px-3 rounded-md text-[12px] font-medium transition-colors ' +
                            (active
                                ? 'bg-[#4B3FD8] text-white'
                                : 'text-[#6B7280] hover:text-[#1A1D2E] hover:bg-[#F5F6FA]')
                        }
                    >
                        {label}
                    </button>
                );
            })}

            {openCustom && (
                <div
                    role="dialog"
                    data-testid={`${testId}-custom-popover`}
                    className="absolute top-full right-0 mt-2 z-30 w-[300px] bg-white border border-[#E5E7EB] rounded-xl shadow-nivo-hover p-4"
                >
                    <div className="flex flex-col gap-3">
                        <label className="text-xs text-[#6B7280] flex flex-col gap-1">
                            Du
                            <input
                                type="date"
                                value={draftFrom}
                                onChange={(e) => setDraftFrom(e.target.value)}
                                className="h-9 px-2 rounded-md border border-[#E5E7EB] text-sm text-[#1A1D2E]"
                                data-testid={`${testId}-from`}
                            />
                        </label>
                        <label className="text-xs text-[#6B7280] flex flex-col gap-1">
                            Au
                            <input
                                type="date"
                                value={draftTo}
                                onChange={(e) => setDraftTo(e.target.value)}
                                className="h-9 px-2 rounded-md border border-[#E5E7EB] text-sm text-[#1A1D2E]"
                                data-testid={`${testId}-to`}
                            />
                        </label>
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setOpenCustom(false)}
                                className="h-8 px-3 rounded-md text-sm text-[#6B7280] hover:bg-[#F5F6FA]"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={applyCustom}
                                disabled={!draftFrom || !draftTo}
                                data-testid={`${testId}-apply`}
                                className="h-8 px-3 rounded-md text-sm font-semibold text-white bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] disabled:opacity-50"
                            >
                                Appliquer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
